import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "../../contexts/Translation";
import { categoryLabel } from "../Dashboard/labels";
import JourneyBand, { JourneyScrim } from "../Decor/JourneyBand";
import ProfileImage from "../ProfileImage";
import ConclusionFlag from "./ConclusionFlag";
import styles from "./Details.module.css";

type HeroGoal = {
  name: string;
  status?: string;
  categories: string[];
  isSuccess?: string;
  profileImage?: string;
};

type GoalHeroProps = {
  goal: HeroGoal;
  backTo: string;
  backLabel: string;
  /** The `<li>` items under the title: start, end and the baseline. */
  facts: ReactNode;
  isOwner: boolean;
  isEdit: boolean;
  onToggleEdit: () => void;
};

/** The petrol top of a goal page, shared by improvement goals and organization goals. */
const GoalHero = ({ goal, backTo, backLabel, facts, isOwner, isEdit, onToggleEdit }: GoalHeroProps) => {
  const t = useTranslations("improvementGoals");
  const categories = goal.categories.filter(Boolean);

  return (
    <header className={styles.hero}>
      {/* Still while the owner fills in the edit form below. */}
      <JourneyBand curve='settle' quiet wave={isEdit ? undefined : "settleGoal"} />
      <JourneyScrim />
      <Link className={styles.back} to={backTo}>
        ← {backLabel}
      </Link>
      <div className={styles.badges}>
        <span className={styles.statusBadge}>{t(`status_${goal.status}`)}</span>
        {categories.length > 0 && <span className={styles.badge}>{t("categoryBadge", categories.map((c) => t(categoryLabel(c))).join(", "))}</span>}
      </div>
      <div className={styles.titleRow}>
        {goal.profileImage && <ProfileImage src={`/files/profile/${goal.profileImage}`} variant='medium' alt='' />}
        <h1 className={styles.title}>
          {goal.name || t("goalHeading")} <ConclusionFlag isSuccess={goal.isSuccess} />
        </h1>
      </div>
      <ul className={styles.facts}>{facts}</ul>
      <div className={styles.actions}>
        {isOwner && (
          <button type='button' className={styles.secondary} aria-pressed={isEdit} onClick={onToggleEdit}>
            {t("editGoal")}
          </button>
        )}
        <Link className={styles.primary} to='/survey'>
          {t("createSurvey")}
        </Link>
      </div>
    </header>
  );
};

export default GoalHero;
